<template>
  <div class="five-template">
    <!-- Название проекта -->
    <div class="five-template__header">
      <BaseTypography with-pre-line type="h1" weight="600">
        {{ data.title }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.description }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.header"
        :button="buttons.header"
      />
    </div>

    <!-- Три изображения в ряд -->
    <div class="five-template__triple-images">
      <div class="five-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple1"
          :src="data.images.triple1"
          :aspect-width="427"
          :aspect-height="390"
        />
      </div>
      <div class="five-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple2"
          :src="data.images.triple2"
          :aspect-width="427"
          :aspect-height="390"
        />
      </div>
      <div class="five-template__triple-image">
        <TemplateMediaView
          v-if="data.images.triple3"
          :src="data.images.triple3"
          :aspect-width="427"
          :aspect-height="390"
        />
      </div>
    </div>

    <div class="five-template__two-section">
      <div class="five-template__additional-section">
        <BaseButton
          v-if="!buttons.leftSection && !props.templateData"
          variant="dark"
          rounded
          class="five-template__section-marker"
        >
          1
        </BaseButton>
        <BaseTypography with-pre-line type="h3">
          {{ data.leftSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.leftSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.leftSection"
          full-width
          :button="buttons.leftSection"
        />
      </div>
      <div class="five-template__additional-section">
        <BaseButton
          v-if="!buttons.rightSection && !props.templateData"
          variant="dark"
          rounded
          class="five-template__section-marker"
        >
          2
        </BaseButton>
        <BaseTypography with-pre-line type="h3">
          {{ data.rightSectionTitle }}
        </BaseTypography>
        <BaseTypography with-pre-line type="text" weight="400">
          {{ data.rightSectionDescription }}
        </BaseTypography>
        <TemplateLandingButtonView
          v-if="buttons.rightSection"
          full-width
          :button="buttons.rightSection"
        />
      </div>
    </div>

    <div class="five-template__additional-section">
      <BaseTypography with-pre-line type="h3">
        {{ data.bottomSectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.bottomSectionDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.additional"
        :button="buttons.additional"
      />
    </div>

    <div class="five-template__two-section">
      <div class="five-template__bottom-image">
        <TemplateMediaView
          v-if="data.images.bottom1"
          :src="data.images.bottom1"
          :aspect-width="642"
          :aspect-height="280"
        />
      </div>
      <div class="five-template__bottom-image">
        <TemplateMediaView
          v-if="data.images.bottom2"
          :src="data.images.bottom2"
          :aspect-width="642"
          :aspect-height="280"
        />
      </div>
    </div>

    <div class="five-template__additional-section">
      <BaseTypography with-pre-line type="h3">
        {{ data.bottomSecondarySectionTitle }}
      </BaseTypography>
      <BaseTypography with-pre-line type="text" weight="400">
        {{ data.bottomSecondaryDescription }}
      </BaseTypography>
      <TemplateLandingButtonView
        v-if="buttons.secondaryAdditional"
        :button="buttons.secondaryAdditional"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { IFiveTemplateData, TemplateData } from '~/entities/projects/model/templates-interface'
import { computed } from 'vue'
import { useTemplateViewButtons } from '~/entities/projects/model/useTemplateViewButtons'
import TemplateLandingButtonView from '~/entities/projects/ui/templates/TemplateLandingButtonView.vue'
import TemplateMediaView from '~/entities/projects/ui/templates/TemplateMediaView.vue'
import { BaseButton, BaseTypography } from '~/shared/ui'

interface Props {
  templateData?: TemplateData | null
}

const props = withDefaults(defineProps<Props>(), {
  templateData: null,
})

const data = computed(() => {
  if (props.templateData) {
    return props.templateData as IFiveTemplateData
  }

  return {
    title: 'Как заполнить этот шаблон',
    description: 'Заголовок — напишите короткую фразу из 3–7 слов. Она должна быть простой и понятной. Заголовок должен передавать позитивное настроение. Например: про новый день, вдохновение, движение вперёд, радость, развитие. Не используйте сложные слова и длинные предложения.',
    leftSectionTitle: 'Подзаголовок',
    leftSectionDescription: 'Напишите текст объёмом до 1000 символов. Лучше разделить его на 2–3 абзаца, чтобы текст было удобно читать. Один абзац — это 2–4 предложения. Пишите простым языком. Представьте, что вы объясняете мысль другу. Текст должен быть лёгким, понятным и нейтральным. Он не должен быть привязан к конкретному продукту, компании или услуге. Это универсальный текст, который используется как пример для шаблона.',
    rightSectionTitle: 'Подзаголовок',
    rightSectionDescription: 'Можно писать о вдохновении, новых возможностях, развитии, спокойствии, ценности момента, движении вперёд или простых радостях жизни. Старайтесь: не использовать сложные профессиональные термины, не добавлять агрессивную рекламу, не писать слишком длинные предложения, не перегружать текст цифрами и фактами. Важно, чтобы текст выглядел аккуратно в блоке и легко воспринимался при быстром чтении.',
    bottomSectionTitle: 'Подзаголовок',
    bottomSectionDescription: 'Добавьте одно короткое предложение, которое раскрывает смысл заголовка. Это может быть продолжение мысли или лёгкая мотивационная фраза. Пишите естественно, как будто обращаетесь к обычному человеку. Избегайте сложных слов и пафоса.',
    bottomSecondarySectionTitle: 'Подзаголовок',
    bottomSecondaryDescription: 'Старайтесь не перегружать предложения и не делать их слишком длинными. Лучше разбивать текст на небольшие абзацы по несколько предложений, чтобы его было удобно читать даже при быстром просмотре страницы. Избегайте негативных формулировок, громких обещаний и излишнего пафоса. Текст должен выглядеть естественно и вызывать ощущение спокойствия и лёгкости. Если после прочтения создаётся ощущение ясности и простоты, значит текст подходит для шаблона. Главное — чтобы он был понятен любому человеку независимо от возраста и опыта.',
    images: {
      triple1: '/projects/project-template-6-bg-1.webp',
      triple2: '/projects/project-template-6-bg-2.webp',
      triple3: '/projects/project-template-6-bg-3.webp',
      bottom1: '/projects/project-template-6-bg-4.webp',
      bottom2: '/projects/project-template-6-bg-5.webp',
    },
  }
})

const buttons = useTemplateViewButtons(() => props.templateData)
</script>

<style lang="scss" scoped>
.five-template {
  --triple-image-max-width: 100%;
  --bottom-image-max-width: 100%;

  display: grid;
  gap: rem(60px);
  grid-template-columns: 1fr;

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(40px);
  }

  > * {
    min-width: 0;
  }

  &__header {
    display: grid;
    text-align: center;
    gap: rem(16px);

    > * {
      min-width: 0;
      max-width: 100%;
    }

    @include bp-media('xs', 'sm') {
      :deep(.base-typography--type-h2) {
        font-size: rem(22px);
      }
    }
  }

  &__main-section {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm' ) {
      :deep(.base-typography--type-h2) {
        font-size: rem(22px);
      }

      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__two-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(28px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs') {
      grid-template-columns: 1fr;
    }
  }

  &__main-image {
    display: flex;
  }

  &__side-images {
    display: grid;
    gap: rem(20px);
  }

  &__dual-headers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__header-left,
  &__header-right {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }
  }

  &__triple-images {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);

    > * {
      min-width: 0;
    }

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }

  }

  &__section-marker {
    width: rem(56px);
    height: rem(56px);
  }

  &__triple-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

  &__bottom-image {
    overflow: hidden;
    border-radius: rem(16px);
    display: grid;
  }

}
</style>
